import LineItemFormField from '@/Enums/CampaignManagement/LineItemFormField.enum';
import Color from '@/Enums/Color.enum';
import SearchIcon from '@/components/Icons/SearchIcon';
import StyledInput from '@/components/TabSelect/styled/StyledInput';
import { GREY } from '@/constants/color';
import { Close } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import classNames from 'classnames';
import { type ChangeEvent, type KeyboardEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

const ALLOW_INSTRUCTIONS =
  'Please upload a csv file of all domains that you would like to INCLUDE on this line item. Do not include anything else in the csv, only a list of domains in column A.';
const BLOCK_INSTRUCTIONS =
  'Please upload a csv file of all domains that you would like to EXCLUDE or BLOCK on this line item. Do not include anything else in the csv, only a list of domains in column A.';

function parseCsvDomains(text: string): string[] {
  return text
    .split(/[\r\n]+/)
    .map(line => line.split(',')[0]?.trim().toLowerCase())
    .filter((domain): domain is string => !!domain && domain !== 'domain');
}

function deduplicateDomains(
  allowDomains: string[],
  blockDomains: string[],
): { allowDomains: string[]; blockDomains: string[] } {
  const blockSet = new Set(blockDomains);
  return {
    allowDomains: allowDomains.filter(d => !blockSet.has(d)),
    blockDomains,
  };
}

interface DomainColumnProps {
  instructions: string;
  domains: string[];
  onUpload: (domains: string[]) => void;
  onRemove: (index: number) => void;
  onClear: () => void;
}

const DomainColumn = ({ instructions, domains, onUpload, onRemove, onClear }: DomainColumnProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = e => {
        const text = e.target?.result as string;
        const parsed = parseCsvDomains(text);
        if (parsed.length) {
          onUpload(parsed);
        }
      };
      reader.readAsText(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onUpload],
  );

  const handleAddDomain = useCallback(() => {
    const domain = inputValue.trim().toLowerCase();
    if (domain) {
      onUpload([domain]);
      setInputValue('');
    }
  }, [inputValue, onUpload]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleAddDomain();
      }
    },
    [handleAddDomain],
  );

  return (
    <Box className='flex flex-col h-full'>
      <Box className='flex items-center justify-end px-3 py-2 gap-1'>
        <Box className='flex gap-1'>
          <Button
            size='small'
            variant='outlined'
            onClick={() => fileInputRef.current?.click()}
            sx={{ fontSize: '11px', py: '2px', px: '6px', minWidth: 0 }}
          >
            Upload CSV
          </Button>
          {domains.length > 0 && (
            <Button
              size='small'
              variant='outlined'
              color='error'
              onClick={onClear}
              sx={{ fontSize: '11px', py: '2px', px: '6px', minWidth: 0 }}
            >
              Clear
            </Button>
          )}
        </Box>
        <input ref={fileInputRef} type='file' accept='.csv' hidden onChange={handleFileChange} />
      </Box>
      <Typography variant='caption' className='px-3 pb-1' sx={{ color: Color.Grey, fontSize: '11px', lineHeight: 1.3 }}>
        {instructions}
      </Typography>
      <Box className='px-3 pb-1'>
        <StyledInput
          fullWidth
          startAdornment={<SearchIcon className={`text-[${GREY}]`} />}
          placeholder='Type domain and press Enter'
          value={inputValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ padding: '0 7px 0 16px', fontSize: '13px' }}
        />
      </Box>
      <Box className='flex-1 overflow-y-auto px-3 pb-2'>
        {domains.map((domain, i) => (
          <Box
            key={`${domain}-${i}`}
            className='flex items-center justify-between group hover:bg-[#f5f5f5] rounded px-1'
          >
            <Typography variant='body2' className='font-[Manrope] text-[14px] text-[#303030] py-0.5'>
              {domain}
            </Typography>
            <IconButton
              size='small'
              onClick={() => onRemove(i)}
              sx={{ opacity: 0, '.group:hover &': { opacity: 1 }, p: '2px' }}
            >
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const VALIDATED_FIELDS = [
  LineItemFormField.CustomAllowDomains,
  LineItemFormField.CustomBlockDomains,
  LineItemFormField.Product,
];

const CustomDomainLists = () => {
  const { control, setValue, trigger } = useFormContext();

  const [allowDomains, blockDomains]: [string[], string[]] = useWatch({
    control,
    name: [LineItemFormField.CustomAllowDomains, LineItemFormField.CustomBlockDomains],
    defaultValue: [[], []],
  });

  const currentAllow = useMemo(() => allowDomains ?? [], [allowDomains]);
  const currentBlock = useMemo(() => blockDomains ?? [], [blockDomains]);

  const setDomains = useCallback(
    (nextAllow: string[], nextBlock: string[]) => {
      setValue(LineItemFormField.CustomAllowDomains, nextAllow, { shouldDirty: true });
      setValue(LineItemFormField.CustomBlockDomains, nextBlock, { shouldDirty: true });
      trigger(VALIDATED_FIELDS);
    },
    [setValue, trigger],
  );

  const handleAllowUpload = useCallback(
    (newDomains: string[]) => {
      const merged = [...new Set([...currentAllow, ...newDomains])];
      const { allowDomains: deduped } = deduplicateDomains(merged, currentBlock);
      setDomains(deduped, currentBlock);
    },
    [currentAllow, currentBlock, setDomains],
  );

  const handleBlockUpload = useCallback(
    (newDomains: string[]) => {
      const mergedBlock = [...new Set([...currentBlock, ...newDomains])];
      const { allowDomains: dedupedAllow } = deduplicateDomains(currentAllow, mergedBlock);
      setDomains(dedupedAllow, mergedBlock);
    },
    [currentAllow, currentBlock, setDomains],
  );

  const handleAllowRemove = useCallback(
    (index: number) =>
      setDomains(
        currentAllow.filter((_, i) => i !== index),
        currentBlock,
      ),
    [currentAllow, currentBlock, setDomains],
  );

  const handleBlockRemove = useCallback(
    (index: number) =>
      setDomains(
        currentAllow,
        currentBlock.filter((_, i) => i !== index),
      ),
    [currentAllow, currentBlock, setDomains],
  );

  const handleAllowClear = useCallback(() => setDomains([], currentBlock), [currentBlock, setDomains]);
  const handleBlockClear = useCallback(() => setDomains(currentAllow, []), [currentAllow, setDomains]);

  return (
    <div className={classNames('flex flex-col border border-[#E8E8E8] rounded-md')}>
      <div className='w-full flex bg-[#E8E8E8]/40 rounded-t-lg'>
        <p className={`w-1/2 px-4 py-2 border-r font-[Manrope] text-[${Color.Grey}] text-[14px] font-semibold`}>
          Allow List
        </p>
        <p className={`w-1/2 px-4 py-2 font-[Manrope] text-[${Color.Grey}] text-[14px] font-semibold`}>Block List</p>
      </div>
      <div className='flex h-72'>
        <Box className='w-1/2 border-r overflow-hidden flex flex-col'>
          <DomainColumn
            instructions={ALLOW_INSTRUCTIONS}
            domains={currentAllow}
            onUpload={handleAllowUpload}
            onRemove={handleAllowRemove}
            onClear={handleAllowClear}
          />
        </Box>
        <Box className='w-1/2 overflow-hidden flex flex-col'>
          <DomainColumn
            instructions={BLOCK_INSTRUCTIONS}
            domains={currentBlock}
            onUpload={handleBlockUpload}
            onRemove={handleBlockRemove}
            onClear={handleBlockClear}
          />
        </Box>
      </div>
    </div>
  );
};

export default CustomDomainLists;
